import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { AppIconButton } from 'src/component/common/analog/molecule/button/AppIconButton'
import { useBool } from '../../../../hook/bool/useBool'
import { usePortfolioStore } from '../../store/portfolio.store'
import { PortfolioType, UpdatePortfolioDto } from '../../model/portfolio'
import { DeleteConfirmationModal } from '../template/DeleteConfirmationModal'
import { BioFormModal } from 'src/feature/profile/component/template/BioFormModal'
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export type BioCardProps = {
  userId?: string
}

export const BioCard = observer(({ userId }: BioCardProps) => {
  const { translation } = useLocalizationStore()
  const portfolioStore = usePortfolioStore()
  const formModalBool = useBool()
  const deleteModalBool = useBool()
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    portfolioStore.reset()
    portfolioStore.handleGetPortfolio(userId)
  }, [portfolioStore, userId])

  const { isLoading } = portfolioStore.getGenericFieldsState('handleGetPortfolio')
  const { isLoading: isUpdating } = portfolioStore.getGenericFieldsState('handleUpdateBio')
  const { isLoading: isDeleting } = portfolioStore.getGenericFieldsState('handleDeletePortfolioItem')

  const isOperationInProgress = isUpdating || isDeleting

  const handleSubmitBio = async (data: UpdatePortfolioDto) => {
    await portfolioStore.handleUpdateBio(data)
    formModalBool.makeFalse()
    setIsEditing(false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
    formModalBool.makeTrue()
  }

  const handleAddClick = () => {
    setIsEditing(false)
    formModalBool.makeTrue()
  }

  const handleDeleteClick = () => {
    deleteModalBool.makeTrue()
  }

  const handleConfirmDelete = async () => {
    if (portfolioStore.bio) {
      await portfolioStore.handleDeletePortfolioItem(portfolioStore.bio.id, PortfolioType.BIO)
    }
  }

  const hasBio = portfolioStore.hasBio
  const bio = portfolioStore.bio

  return (
    <>
      <AppPaper sx={styles.root}>
        <AppBox width='100%' justifyContent='space-between'>
          <Typography variant='h5'>{translation.pages.profileInfo.careerGoalsCard.bio()}</Typography>
          {!userId && (
            <AppBox sx={styles.actionButtons}>
              {hasBio ? (
                <>
                  <AppIconButton
                    sx={{
                      ...styles.iconButton,
                      color: ColorTokens.primary500,
                      backgroundColor: ColorTokens.primary10,
                    }}
                    onClick={handleEditClick}
                    disabled={isOperationInProgress}
                  >
                    <EditNoteRoundedIcon />
                  </AppIconButton>
                  <AppIconButton
                    sx={{
                      ...styles.iconButton,
                      color: ColorTokens.red200,
                      backgroundColor: ColorTokens.red10,
                    }}
                    onClick={handleDeleteClick}
                    disabled={isOperationInProgress}
                  >
                    <DeleteOutlineRoundedIcon />
                  </AppIconButton>
                </>
              ) : (
                <AppButton
                  fontSize={12}
                  textValue={translation.buttons.add()}
                  variant="light"
                  onClick={handleAddClick}
                  disabled={isOperationInProgress}
                />
              )}
            </AppBox>
          )}
        </AppBox>
        
        {isLoading ? (
          <AppBox sx={styles.bioContainer}>
            <AppSkeleton
              count={1}
              direction="column"
              rects={[
                { width: '100%', height: 60 },
              ]}
            />
          </AppBox>
        ) : hasBio && bio ? (
          <AppBox sx={styles.bioContainer}>
            <Typography sx={styles.bioText}>
              {bio.content}
            </Typography>
            <Typography sx={styles.bioDate}>
              {new Date(bio.updatedAt).toLocaleDateString()}
            </Typography>
          </AppBox>
        ) : (
          <Caption sx={{ color: 'text.secondary', fontSize: 11 }}>
            {translation.noData()}
          </Caption>
        )}
      </AppPaper>

      <BioFormModal
        {...formModalBool}
        onSubmit={handleSubmitBio}
        initialData={isEditing ? bio : undefined}
        isEditing={isEditing}
      />

      <DeleteConfirmationModal
        {...deleteModalBool}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title={translation.pages.profile.portfolioModal.deleteConfirmTitle()}
        description={translation.pages.profile.portfolioModal.deleteConfirmDescription()}
      />
    </>
  )
})

const styles = {
  root: {
    padding: '10px 15px',
    borderRadius: '10px',
    gap: 10,
    width: '100%',
    border: 0,
  },
  actionButtons: {
    gap: 8,
    alignItems: 'center',
  },
  iconButton: {
    width: 30,
    height: 30,
    borderRadius: '999px',
    transition: 'transform 0.3s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  bioContainer: {
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    height: 270,
    overflow: 'auto',
  },
  bioText: {
    color: 'text.primary',
    fontSize: 12,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  bioDate: {
    color: 'text.secondary',
    fontSize: 10,
    alignSelf: 'flex-end',
  },
}
